import { useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import {
  FeatureFlagsProvider,
  useFeatureFlags,
} from "./FeatureFlagsContext";
import { getMyFeatureFlags } from "../api/platform";

const REFRESH_MS = 20000;

/**
 * Overlays the signed-in company's module switches on the brand flags.
 * Platform admin has no company, so the brand snapshot stays as-is.
 */
export function SessionFeatureFlags({ children }) {
  const brandFlags = useFeatureFlags();
  const { user } = useAuth();
  const [tenantFlags, setTenantFlags] = useState(null);
  const isPlatformAdmin =
    user?.role === "platform-admin" ||
    user?.backendRoles?.includes("platform_admin");

  useEffect(() => {
    if (!user?.id || isPlatformAdmin) {
      setTenantFlags(null);
      return undefined;
    }

    let cancelled = false;

    async function load() {
      try {
        const data = await getMyFeatureFlags();
        if (!cancelled && data?.flags && typeof data.flags === "object") {
          setTenantFlags(data.flags);
        }
      } catch {
        // Brand flags stay in place. A failed lookup must not hide modules.
      }
    }

    load();
    const timer = window.setInterval(load, REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("app:resume", load);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("app:resume", load);
    };
  }, [user?.id, isPlatformAdmin]);

  const flags = useMemo(
    () => (tenantFlags ? { ...brandFlags, ...tenantFlags } : brandFlags),
    [brandFlags, tenantFlags],
  );

  return <FeatureFlagsProvider flags={flags}>{children}</FeatureFlagsProvider>;
}
